import type { DqCheckEntry } from '@/features/adp/constants/dataQualityChecks'
import type { PersistedSourceConfigurationState } from '@/features/adp/hooks/useFinalizeSourceConfigurationMutation'
import type {
  SourceFileFieldPayloadItem,
  SourceFilePayloadItem,
  SourceFilesBody,
} from '@/features/adp/services/source/source.types'
import { DataSourceType } from '@/features/adp/types/data-source.types'
import { directoryOf } from '@/features/adp/utils/fileExtension.util'

export type ExplorerSelectedColumn = {
  selected: boolean
  columnName: string
  dataType: string
  primaryKey: boolean
  dqChecks: DqCheckEntry[]
}

export type ExplorerFileExtras = {
  fileDelimiter?: string
  jsonPath?: string
  dateFormat?: string
}

type BuildSourceFilePayloadParams = {
  sourceEntry: PersistedSourceConfigurationState | null
  sourceName: string
  selectedTable: string
  rows: ExplorerSelectedColumn[]
  sourceType: string
  fileExtras?: ExplorerFileExtras
}

function computeSourceFileType(
  dataSourceType: string,
  selectedTable: string,
  sourceInstance: string,
  sourceConfigurationState: PersistedSourceConfigurationState | null
): string {
  const normalizedType = dataSourceType.trim().toUpperCase()
  const normalizedInstance = sourceInstance.trim().toLowerCase()

  if (normalizedType === DataSourceType.API || normalizedType === DataSourceType.REAL_TIME_SOURCE) {
    return 'API'
  }

  if (normalizedType === DataSourceType.FILE || normalizedType === DataSourceType.FILESYSTEM) {
    if (normalizedType === DataSourceType.FILE && normalizedInstance === 'excel') {
      const excelSheetPath = resolveExcelTablePath(sourceConfigurationState)
      const extension = selectedExtensionOrDefault(excelSheetPath)
      return extension === 'table' ? 'table' : extension
    }

    const extension = selectedExtensionOrDefault(selectedTable)
    return extension === 'table' ? 'table' : extension
  }

  if (normalizedType === DataSourceType.NO_SQL || normalizedType === DataSourceType.CONNECTION) {
    return 'connection'
  }

  if (normalizedType === DataSourceType.RDBMS) {
    return 'table'
  }

  return 'table'
}

function computeSourceFileFormat(dataSourceType: string): string {
  const normalizedType = dataSourceType.trim().toUpperCase()
  if (normalizedType === DataSourceType.API || normalizedType === DataSourceType.REAL_TIME_SOURCE) {
    // In case of API or Real-Time Source, we default to JSON
    return 'JSON'
  }

  return dataSourceType.trim()
}

function selectedExtensionOrDefault(fileName: string): string {
  const trimmed = fileName.trim()
  if (!trimmed.includes('.')) return 'table'
  return `.${trimmed.split('.').pop()?.toLowerCase() ?? 'csv'}`
}

function resolveExcelTablePath(
  sourceConfigurationState: PersistedSourceConfigurationState | null
): string {
  const submittedPayload =
    (sourceConfigurationState?.submitted_source_payload as Record<string, unknown> | undefined) ??
    {}
  const tablePath = extractStringOrEmpty(submittedPayload.table_path)
  if (tablePath) return tablePath

  const sheetPaths = sourceConfigurationState?.sheetPaths ?? []
  return sheetPaths[0] ?? ''
}

function directoryPathFromFilePath(path: string): string {
  const normalized = path.trim()
  if (!normalized) return ''
  const lastSlashIndex = normalized.lastIndexOf('/')
  if (lastSlashIndex <= 0) return normalized
  return normalized.slice(0, lastSlashIndex)
}

function computeSourcePath(
  dataSourceType: string,
  sourceInstance: string,
  selectedTable: string,
  sourceConfigurationState: PersistedSourceConfigurationState | null
): string {
  const submittedPayload =
    (sourceConfigurationState?.submitted_source_payload as Record<string, unknown> | undefined) ??
    {}
  const normalizedType = dataSourceType.trim().toUpperCase()
  const normalizedInstance = sourceInstance.trim().toLowerCase()

  if (normalizedType === DataSourceType.FILE && normalizedInstance === 'excel') {
    const excelSheetPath = resolveExcelTablePath(sourceConfigurationState)
    return directoryPathFromFilePath(excelSheetPath)
  }

  if (normalizedType === DataSourceType.FILESYSTEM) {
    return directoryOf(selectedTable)
  }

  return toSerializedValue(submittedPayload.api_path)
}

function extractStringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function toSerializedValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (value == null) return ''
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

function toApiObjectOrEmpty(
  value: unknown,
  isApiSource: boolean
): Record<string, unknown> | string {
  if (!isApiSource) return ''
  if (value == null) return ''

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return ''
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
    } catch {
      return ''
    }
  }

  return ''
}

export function buildCreateSourceFileAndFieldsPayload({
  sourceEntry,
  sourceName,
  selectedTable,
  rows,
  sourceType,
  fileExtras,
}: BuildSourceFilePayloadParams): SourceFilesBody | null {
  const selectedRows = rows.filter(row => row.selected)
  if (selectedRows.length === 0) return null

  const submittedPayload =
    (sourceEntry?.submitted_source_payload as Record<string, unknown> | undefined) ?? {}
  const sourceInstance = typeof submittedPayload.type === 'string' ? submittedPayload.type : ''
  const normalizedSourceType = (sourceType || '').trim()
  const isApiSource = normalizedSourceType.toUpperCase() === DataSourceType.API
  const sourceFileType = computeSourceFileType(
    normalizedSourceType,
    selectedTable,
    sourceInstance,
    sourceEntry
  )
  const sourceFileFormat = computeSourceFileFormat(normalizedSourceType)
  const sourcePath = computeSourcePath(
    normalizedSourceType,
    sourceInstance,
    selectedTable,
    sourceEntry
  )

  const fileDelimiter =
    fileExtras?.fileDelimiter?.trim() || toSerializedValue(submittedPayload.file_delimiter)

  const sourceFile: SourceFilePayloadItem[] = [
    {
      data_source_name: sourceName,
      data_source_instance: sourceInstance,
      file_name: selectedTable,
      source_file_format: sourceFileFormat,
      source_file_type: sourceFileType,
      authentication_method: '',
      request_type: toSerializedValue(submittedPayload.api_method), // For API (Methods: GET or POST)
      headers: toApiObjectOrEmpty(submittedPayload.api_headers, isApiSource),
      payload_query: toApiObjectOrEmpty(submittedPayload.api_body, isApiSource),
      params: toApiObjectOrEmpty(submittedPayload.api_params, isApiSource),
      path: sourcePath,
      file_delimiter: fileDelimiter,
    },
  ]

  const sourceFileFields: SourceFileFieldPayloadItem[] = selectedRows.map(row => ({
    file_name: selectedTable,
    field_name: row.columnName,
    field_data_type: row.dataType,
    field_structure: '',
    field_size: row.dataType === 'string' ? '1024' : '0',
    precision: 0,
    is_business_primary_key: row.primaryKey,
    dq_check: row.dqChecks.map(check => check.value).join(', '),
  }))

  if (sourceFileFields.length === 0) return null

  return {
    source_file: sourceFile,
    source_file_field: sourceFileFields,
  }
}
